import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, 
  Euro, 
  Calendar, 
  Edit, 
  Trash2, 
  TrendingUp, 
  TrendingDown,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  MoreVertical,
  Palette,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, withRetry } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserSubscription {
  id: string;
  name: string;
  provider: string;
  price: number;
  currency: string;
  billing_cycle: 'monthly' | 'yearly' | 'weekly' | 'quarterly';
  is_active: boolean;
  next_billing_date?: string;
  category: string;
  logo_color: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface SubscriptionProvider {
  id: string;
  name: string;
  category: string;
  logo_color: string;
  common_prices: Array<{
    price: number;
    cycle: string;
    plan: string;
  }>;
}

const BILLING_CYCLES = {
  weekly: { label: 'Wöchentlich', multiplier: 52 },
  monthly: { label: 'Monatlich', multiplier: 12 },
  quarterly: { label: 'Vierteljährlich', multiplier: 4 },
  yearly: { label: 'Jährlich', multiplier: 1 }
};

const CATEGORIES = [
  'Streaming',
  'Musik',
  'Produktivität',
  'Design',
  'Cloud Storage',
  'Kommunikation',
  'Entwicklung',
  'KI',
  'Shopping & Streaming',
  'Sonstiges'
];

const PRESET_COLORS = [
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
  '#f43f5e', '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4',
  '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7'
];

export function SubscriptionOverview() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [providers, setProviders] = useState<SubscriptionProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<UserSubscription | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isCustomProvider, setIsCustomProvider] = useState(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    price: '',
    currency: 'EUR',
    billing_cycle: 'monthly' as const,
    is_active: true,
    next_billing_date: '',
    category: 'Sonstiges',
    logo_color: '#6366f1',
    notes: ''
  });

  useEffect(() => {
    if (user) {
      fetchSubscriptions();
      fetchProviders();
    }
  }, [user]);

  const fetchSubscriptions = async () => {
    if (!user) return;
    
    try {
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
      });

      setSubscriptions(data || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Fehler beim Laden der Abonnements');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from('subscription_providers')
          .select('*')
          .order('name');

        if (error) throw error;
        return data;
      });

      setProviders(data || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      provider: '',
      price: '',
      currency: 'EUR',
      billing_cycle: 'monthly',
      is_active: true,
      next_billing_date: '',
      category: 'Sonstiges',
      logo_color: '#6366f1',
      notes: ''
    });
    setIsCustomProvider(false);
    setShowAdvancedFields(false);
  };

  const handleProviderSelect = (providerName: string) => {
    if (providerName === 'custom') {
      setIsCustomProvider(true);
      setFormData(prev => ({
        ...prev,
        provider: '',
        name: '',
        category: 'Sonstiges',
        logo_color: '#6366f1'
      }));
    } else {
      setIsCustomProvider(false);
      const provider = providers.find(p => p.name === providerName);
      if (provider) {
        setFormData(prev => ({
          ...prev,
          provider: providerName,
          name: providerName,
          category: provider.category,
          logo_color: provider.logo_color
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.name || !formData.price) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Für eigene Anbieter: provider = name
    const finalProvider = isCustomProvider ? formData.name : formData.provider;

    try {
      const numericPrice = parseFloat(formData.price.replace(',', '.'));
      
      const subscriptionData = {
        user_id: user.id,
        name: formData.name,
        provider: finalProvider,
        price: numericPrice,
        currency: formData.currency,
        billing_cycle: formData.billing_cycle,
        is_active: formData.is_active,
        next_billing_date: formData.next_billing_date || null,
        category: formData.category,
        logo_color: formData.logo_color,
        notes: formData.notes || null
      };

      if (editingSubscription) {
        // Update existing subscription
        await withRetry(async () => {
          const { error } = await supabase
            .from('user_subscriptions')
            .update(subscriptionData)
            .eq('id', editingSubscription.id)
            .eq('user_id', user.id);

          if (error) throw error;
          return true;
        });

        toast.success('Abonnement erfolgreich aktualisiert');
        setIsEditDialogOpen(false);
        setEditingSubscription(null);
      } else {
        // Create new subscription
        await withRetry(async () => {
          const { error } = await supabase
            .from('user_subscriptions')
            .insert([subscriptionData]);

          if (error) throw error;
          return true;
        });

        toast.success('Abonnement erfolgreich hinzugefügt');
        setIsAddDialogOpen(false);
      }

      resetForm();
      fetchSubscriptions();
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast.error('Fehler beim Speichern des Abonnements');
    }
  };

  const handleEdit = (subscription: UserSubscription) => {
    setEditingSubscription(subscription);
    
    // Check if this is a custom provider (not in predefined list)
    const isCustom = !providers.some(p => p.name === subscription.provider);
    setIsCustomProvider(isCustom);
    
    // Show advanced fields if they have values
    const hasAdvancedData = subscription.next_billing_date || subscription.notes;
    setShowAdvancedFields(hasAdvancedData);
    
    setFormData({
      name: subscription.name,
      provider: isCustom ? '' : subscription.provider,
      price: subscription.price.toString(),
      currency: subscription.currency,
      billing_cycle: subscription.billing_cycle,
      is_active: subscription.is_active,
      next_billing_date: subscription.next_billing_date || '',
      category: subscription.category,
      logo_color: subscription.logo_color,
      notes: subscription.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = async (subscriptionId: string) => {
    if (!user) return;

    try {
      await withRetry(async () => {
        const { error } = await supabase
          .from('user_subscriptions')
          .delete()
          .eq('id', subscriptionId)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      });

      toast.success('Abonnement erfolgreich gelöscht');
      fetchSubscriptions();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Fehler beim Löschen des Abonnements');
    }
  };

  const toggleStatus = async (subscriptionId: string, currentStatus: boolean) => {
    if (!user) return;

    try {
      await withRetry(async () => {
        const { error } = await supabase
          .from('user_subscriptions')
          .update({ is_active: !currentStatus })
          .eq('id', subscriptionId)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      });

      toast.success(`Abonnement ${!currentStatus ? 'aktiviert' : 'deaktiviert'}`);
      fetchSubscriptions();
    } catch (error) {
      console.error('Error toggling subscription status:', error);
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const calculateMonthlyTotal = () => {
    return subscriptions
      .filter(sub => sub.is_active)
      .reduce((total, sub) => {
        // Umrechnung auf monatliche Kosten
        let monthlyPrice;
        switch (sub.billing_cycle) {
          case 'weekly':
            monthlyPrice = sub.price * 4.33; // 52 Wochen / 12 Monate ≈ 4.33
            break;
          case 'monthly':
            monthlyPrice = sub.price;
            break;
          case 'quarterly':
            monthlyPrice = sub.price / 3;
            break;
          case 'yearly':
            monthlyPrice = sub.price / 12;
            break;
          default:
            monthlyPrice = sub.price;
        }
        return total + monthlyPrice;
      }, 0);
  };

  const calculateYearlyTotal = () => {
    return subscriptions
      .filter(sub => sub.is_active)
      .reduce((total, sub) => {
        // Umrechnung auf jährliche Kosten
        const yearlyPrice = sub.price * BILLING_CYCLES[sub.billing_cycle].multiplier;
        return total + yearlyPrice;
      }, 0);
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = searchTerm ? (
      sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.category.toLowerCase().includes(searchTerm.toLowerCase())
    ) : true;
    
    const matchesCategory = categoryFilter === 'all' || sub.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && sub.is_active) ||
      (statusFilter === 'inactive' && !sub.is_active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getNextBillingInfo = (subscription: UserSubscription) => {
    if (!subscription.next_billing_date) return null;
    
    const nextDate = new Date(subscription.next_billing_date);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: 'Überfällig', color: 'text-red-600' };
    if (diffDays === 0) return { text: 'Heute', color: 'text-orange-600' };
    if (diffDays === 1) return { text: 'Morgen', color: 'text-orange-600' };
    if (diffDays <= 7) return { text: `In ${diffDays} Tagen`, color: 'text-yellow-600' };
    
    return { text: `In ${diffDays} Tagen`, color: 'text-gray-600' };
  };

  const generateLogo = (name: string, color: string) => {
    const initials = name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
    return (
      <div 
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm"
        style={{ backgroundColor: color }}
      >
        {initials}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-white border border-gray-200/60">
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
          ))}
        </div>
        <Card className="bg-white border border-gray-200/60">
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="space-y-2">
                <div className="h-16 bg-gray-200 rounded"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiken */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-gray-600 font-medium">Monatliche Kosten</p>
                <p className="text-3xl font-light text-gray-900">{calculateMonthlyTotal().toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Euro className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-gray-600 font-medium">Jährliche Kosten</p>
                <p className="text-3xl font-light text-gray-900">{calculateYearlyTotal().toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-gray-600 font-medium">Aktive Abos</p>
                <p className="text-3xl font-light text-gray-900">
                  {filteredSubscriptions.filter(s => s.is_active).length}
                </p>
                <p className="text-xs text-gray-500">
                  von {filteredSubscriptions.length} gesamt
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hauptkarte */}
      <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-xl font-light text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                Meine Abonnements
              </CardTitle>
              <CardDescription className="text-gray-600 mt-1">
                Verwalten Sie Ihre Abonnements und behalten Sie die Kosten im Blick
              </CardDescription>
            </div>
            
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={resetForm}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Abo hinzufügen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-white border border-gray-200 rounded-xl shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-gray-900">Neues Abonnement hinzufügen</DialogTitle>
                  <DialogDescription className="text-gray-600">
                    Fügen Sie ein neues Abonnement zu Ihrer Übersicht hinzu
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="provider">Anbieter</Label>
                    <Select 
                      value={isCustomProvider ? 'custom' : formData.provider} 
                      onValueChange={handleProviderSelect}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Anbieter auswählen oder eigenen eingeben" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map(provider => (
                          <SelectItem key={provider.id} value={provider.name}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: provider.logo_color }}
                              />
                              {provider.name}
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">
                          <div className="flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            Eigener Anbieter
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">
                      {isCustomProvider ? 'Anbieter-Name' : 'Abo-Name'}
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={isCustomProvider ? "z.B. Mein Service" : "z.B. Netflix Premium"}
                      required
                    />
                  </div>

                  {isCustomProvider && (
                    <div className="space-y-2">
                      <Label>Logo-Farbe</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={formData.logo_color}
                          onChange={(e) => setFormData(prev => ({ ...prev, logo_color: e.target.value }))}
                          className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="grid grid-cols-10 gap-1">
                            {PRESET_COLORS.map((color, index) => (
                              <button
                                key={index}
                                type="button"
                                className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                                style={{ backgroundColor: color }}
                                onClick={() => setFormData(prev => ({ ...prev, logo_color: color }))}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price">Preis</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billing_cycle">Abrechnungszyklus</Label>
                      <Select value={formData.billing_cycle} onValueChange={(value: any) => setFormData(prev => ({ ...prev, billing_cycle: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(BILLING_CYCLES).map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Kategorie</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label htmlFor="is_active">Aktiv</Label>
                  </div>

                  {/* Erweiterte Optionen */}
                  <div className="border-t pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      {showAdvancedFields ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Erweiterte Optionen ausblenden
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Erweiterte Optionen anzeigen
                        </>
                      )}
                    </Button>

                    {showAdvancedFields && (
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="next_billing_date">Nächste Abrechnung (optional)</Label>
                          <Input
                            id="next_billing_date"
                            type="date"
                            value={formData.next_billing_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, next_billing_date: e.target.value }))}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="notes">Notizen (optional)</Label>
                          <Textarea
                            id="notes"
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Zusätzliche Informationen..."
                            rows={2}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit">
                      Hinzufügen
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filter und Suche */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Abonnements durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {CATEGORIES.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Inaktiv</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Abonnements Liste */}
          {filteredSubscriptions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {subscriptions.length === 0 ? 'Keine Abonnements vorhanden' : 'Keine Abonnements gefunden'}
              </h3>
              <p className="text-gray-600 mb-4">
                {subscriptions.length === 0 
                  ? 'Fügen Sie Ihr erstes Abonnement hinzu, um den Überblick zu behalten'
                  : 'Versuchen Sie andere Suchbegriffe oder Filter'
                }
              </p>
              {subscriptions.length === 0 && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Erstes Abo hinzufügen
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredSubscriptions.map((subscription) => {
                const nextBilling = getNextBillingInfo(subscription);
                
                return (
                  <div
                    key={subscription.id}
                    className={`border rounded-xl transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 ${
                      subscription.is_active 
                        ? 'border-gray-200 bg-white hover:border-emerald-300' 
                        : 'border-gray-200 bg-gray-50/50 opacity-75'
                    }`}
                  >
                    {/* Desktop Layout */}
                    <div className="hidden sm:block p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {generateLogo(subscription.name, subscription.logo_color)}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-gray-900 truncate">
                                {subscription.name}
                              </h4>
                              <Badge 
                                variant={subscription.is_active ? "default" : "secondary"}
                                className={`${
                                  subscription.is_active 
                                    ? 'bg-emerald-500 hover:bg-emerald-600 border-0' 
                                    : 'bg-gray-400 text-white border-0'
                                } rounded-lg`}
                              >
                                {subscription.is_active ? 'Aktiv' : 'Inaktiv'}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className="bg-white border-gray-300 text-gray-700 rounded-lg"
                              >
                                {subscription.category}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Euro className="w-3 h-3" />
                                {subscription.price.toFixed(2)} € / {BILLING_CYCLES[subscription.billing_cycle].label.toLowerCase()}
                              </span>
                              
                              {nextBilling && (
                                <span className={`flex items-center gap-1 ${nextBilling.color}`}>
                                  <Calendar className="w-3 h-3" />
                                  {nextBilling.text}
                                </span>
                              )}
                            </div>
                            
                            {subscription.notes && (
                              <p className="text-sm text-gray-500 mt-1 truncate">
                                {subscription.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-gray-100 rounded-lg transition-all duration-300 border border-gray-200"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border-gray-200 rounded-xl shadow-lg">
                            <DropdownMenuItem 
                              onClick={() => handleEdit(subscription)}
                              className="rounded-lg"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => toggleStatus(subscription.id, subscription.is_active)}
                              className="rounded-lg"
                            >
                              {subscription.is_active ? (
                                <>
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Deaktivieren
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Aktivieren
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(subscription.id)}
                              className="text-red-600 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="sm:hidden p-4">
                      <div className="flex items-start gap-3">
                        {generateLogo(subscription.name, subscription.logo_color)}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                {subscription.name}
                              </h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge 
                                  variant={subscription.is_active ? "default" : "secondary"}
                                  className={`text-xs ${
                                    subscription.is_active 
                                      ? 'bg-emerald-500 hover:bg-emerald-600 border-0' 
                                      : 'bg-gray-400 text-white border-0'
                                  } rounded-lg`}
                                >
                                  {subscription.is_active ? 'Aktiv' : 'Inaktiv'}
                                </Badge>
                                <Badge 
                                  variant="outline" 
                                  className="text-xs bg-white border-gray-300 text-gray-700 rounded-lg"
                                >
                                  {subscription.category}
                                </Badge>
                              </div>
                            </div>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0 hover:bg-gray-100 rounded-lg transition-all duration-300 border border-gray-200"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white border-gray-200 rounded-xl shadow-lg">
                                <DropdownMenuItem 
                                  onClick={() => handleEdit(subscription)}
                                  className="rounded-lg"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Bearbeiten
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => toggleStatus(subscription.id, subscription.is_active)}
                                  className="rounded-lg"
                                >
                                  {subscription.is_active ? (
                                    <>
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Deaktivieren
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Aktivieren
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(subscription.id)}
                                  className="text-red-600 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Löschen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                              <Euro className="w-3 h-3" />
                              {subscription.price.toFixed(2)} € / {BILLING_CYCLES[subscription.billing_cycle].label.toLowerCase()}
                            </div>
                            
                            {nextBilling && (
                              <div className={`flex items-center gap-1 text-sm ${nextBilling.color}`}>
                                <Calendar className="w-3 h-3" />
                                {nextBilling.text}
                              </div>
                            )}
                            
                            {subscription.notes && (
                              <p className="text-xs text-gray-500 line-clamp-2">
                                {subscription.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Abonnement bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie die Details Ihres Abonnements
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Abo-Name"
                required
              />
            </div>

            {isCustomProvider && (
              <div className="space-y-2">
                <Label>Logo-Farbe</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.logo_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo_color: e.target.value }))}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="grid grid-cols-10 gap-1">
                      {PRESET_COLORS.map((color, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => setFormData(prev => ({ ...prev, logo_color: color }))}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-price">Preis</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-billing_cycle">Abrechnungszyklus</Label>
                <Select value={formData.billing_cycle} onValueChange={(value: any) => setFormData(prev => ({ ...prev, billing_cycle: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BILLING_CYCLES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-category">Kategorie</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="edit-is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="edit-is_active">Aktiv</Label>
            </div>

            {/* Erweiterte Optionen */}
            <div className="border-t pt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {showAdvancedFields ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Erweiterte Optionen ausblenden
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Erweiterte Optionen anzeigen
                  </>
                )}
              </Button>

              {showAdvancedFields && (
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-next_billing_date">Nächste Abrechnung (optional)</Label>
                    <Input
                      id="edit-next_billing_date"
                      type="date"
                      value={formData.next_billing_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, next_billing_date: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-notes">Notizen (optional)</Label>
                    <Textarea
                      id="edit-notes"
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Zusätzliche Informationen..."
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingSubscription(null);
                  resetForm();
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit">
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
} 